import { Button, Icon, IconButton, Tooltip } from '@kontentino/ui';
import React, { FC } from 'react';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import useSyncProfileInsights from 'app/modules/insights/hooks/useSyncProfileInsights';
import { Page } from 'app/modules/insights/types/Page';
import { useApolloClient } from '@apollo/client';
import Popup from 'utils/popup';
import ProgressIndicator from 'app/components/ProgressIndicator';
import { useTranslation } from 'react-i18next';
import { useIsSyncDisabled } from 'app/modules/insights/components/syncProfileInsightsButton/hooks/useIsSyncDisabled';

type Props = {
  page: Page;
};

const SyncProfileInsightsButton: FC<Props> = (props) => {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const {
    syncManualTrigger,
    syncAutoTrigger,
    jobStatusProgress,
    lastFinishedAt,
    cleanup,
  } = useSyncProfileInsights({
    id: props.page.id,
    pageType: props.page._type,
  });
  const { isSyncDisabled, remainingMinutes } =
    useIsSyncDisabled(lastFinishedAt);

  function onRefresh() {
    cleanup();
    apolloClient.resetStore();
  }

  async function onSyncData() {
    const { isConfirmed } = await Popup.confirm({
      title: t('insights.syncButton.confirmSyncTitle'),
      text: t('insights.syncButton.confirmSyncText'),
      confirmButtonText: t('insights.syncButton.confirmSyncButton'),
      cancelButtonText: t('cancel'),
    });

    if (isConfirmed) {
      syncManualTrigger.mutate();
    }
  }

  function getTooltipContent() {
    if (syncManualTrigger.isLoading && jobStatusProgress !== null) {
      return t('insights.syncButton.completion', {
        progress: jobStatusProgress.batch.progress,
      });
    }

    if (isSyncDisabled) {
      return t('insights.syncButton.allowedToSyncAgain', {
        count: remainingMinutes,
      });
    }
  }

  function getCanSeeRefreshButton() {
    return syncManualTrigger.data?.completed && syncAutoTrigger.data !== null;
  }

  return getCanSeeRefreshButton() ? (
    <Button
      variant="success"
      onClick={onRefresh}
      className="tw-h-[36px]"
      iconBefore={<Icon icon={faArrowsRotate} />}
    >
      {t('insights.syncButton.refresh')}
    </Button>
  ) : (
    <Tooltip content={getTooltipContent()}>
      <div>
        <IconButton
          className="tw-h-[36px]"
          variant="secondary"
          disabled={isSyncDisabled}
          onClick={syncManualTrigger.isLoading ? undefined : onSyncData}
          icon={
            syncManualTrigger.isLoading ? (
              <ProgressIndicator
                progress={jobStatusProgress?.batch.progress ?? 0}
              />
            ) : (
              <Icon icon={faArrowsRotate} />
            )
          }
        />
      </div>
    </Tooltip>
  );
};

export default SyncProfileInsightsButton;
